import { getRouteApi } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { SelectOption } from '@/components/Select/Select'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { usePagination } from '@/hooks/usePagination'
import useRowSelection from '@/hooks/useRowSelection'
import { toast } from '@/hooks/useToast'
import { getFilterValue } from '@/utils/get-filter-value'
import { getValue } from '@/utils/get-value'

import ArchiveCourseModal from '../components/ArchiveCourseModal'
import BulkRemoveCourseModal from '../components/BulkRemoveCourseModal'
import CoursesManagementListFilters from '../components/CoursesManagementListFilters'
import CoursesManagementTable from '../components/CoursesManagementTable'
import RemoveCourseModal from '../components/RemoveCourseModal'
import type { CoursesManagementFiltersKey } from '../constants/courses-management-filters'
import { useCourses, type CoursesListItem } from '../queries/useCourses'
import {
  useCoursesIds,
  type CoursesIdsListItem
} from '../queries/useCoursesIds'
import type { SimpleCourse } from '../types/simple-course'

const routeApi = getRouteApi('/_auth/courses-management/')

const CoursesManagementListView = () => {
  const pagination = usePagination(routeApi)
  const { page: _, pageSize: __, ...filters } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const { t } = useTranslation(['coursesManagement', 'common'])

  const [archiveCourseModal, setArchiveCourseModal] =
    useState<SimpleCourse | null>(null)
  const [removeCourseModal, setRemoveCourseModal] =
    useState<SimpleCourse | null>(null)
  const [bulkRemoveCourseModal, setBulkRemoveCourseModal] = useState<
    CoursesIdsListItem[] | null
  >(null)

  const params = {
    ...pagination.pagination,
    ids: filters.ids?.map(getValue),
    preferredRoomIds: filters.preferredRoomIds?.map(getValue),
    domainIds: filters.domainIds?.map(getValue),
    educationLevelIds: filters.educationLevelIds
  }

  const {
    data: coursesList,
    isLoading: isCoursesListLoading,
    refetch: refetchCoursesList
  } = useCourses(params)
  const {
    data: coursesIds,
    isLoading: isCoursesIdsLoading,
    refetch: refetchCoursesIds
  } = useCoursesIds(params)
  const {
    data: allCoursesIds,
    isLoading: areAllCoursesIdsLoading,
    refetch: refetchAllCoursesIds
  } = useCoursesIds()

  const isLoading =
    isCoursesListLoading || isCoursesIdsLoading || areAllCoursesIdsLoading

  const refetchData = () => {
    refetchCoursesList()
    refetchCoursesIds()
    refetchAllCoursesIds()
  }

  const rowSelection = useRowSelection({
    count: coursesIds?.length ?? 0,
    itemsToSelect: coursesIds?.map(course => course.id) ?? []
  })

  const handleRemoveSuccess = async () => {
    refetchData()
    rowSelection.clearSelection()
  }

  const handleClearAll = () => {
    navigate({
      search: {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }
  const handleFilterChange = (
    key: CoursesManagementFiltersKey,
    value?: SelectOption<string>[] | string[]
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const handleRemoveClick = (course: CoursesListItem) => {
    if (!course.canBeDeleted) {
      toast({
        variant: 'error',
        title: t('toast.course-can-not-be-removed.title', {
          COURSE: course.displayName
        }),
        description: t('toast.course-can-not-be-removed.description')
      })
      return
    }

    setRemoveCourseModal(course)
  }

  const handleBulkRemoveClick = (ids: string[]) => {
    const courses = ids
      .map(courseId => coursesIds?.find(course => course.id === courseId))
      .filter(course => !!course)

    setBulkRemoveCourseModal(courses)
  }

  return (
    <BasicLayout
      breadcrumbs={[
        {
          title: t('common:navigation.courses-management'),
          to: '/courses-management'
        }
      ]}
      moduleName={t('common:header.administration')}
      header={t('coursesManagement:header.courses-management')}
    >
      <CoursesManagementListFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
      />
      <CoursesManagementTable
        data={coursesList?.list}
        count={coursesList?.count}
        totalCount={allCoursesIds?.length}
        isLoading={isLoading}
        rowSelection={rowSelection}
        onArchiveClick={setArchiveCourseModal}
        onRemoveClick={handleRemoveClick}
        onBulkRemoveClick={handleBulkRemoveClick}
        {...pagination}
      />
      {removeCourseModal ? (
        <RemoveCourseModal
          course={removeCourseModal}
          onOpenChange={setRemoveCourseModal}
          onRemoveSuccess={handleRemoveSuccess}
        />
      ) : null}
      {bulkRemoveCourseModal ? (
        <BulkRemoveCourseModal
          courses={bulkRemoveCourseModal}
          onOpenChange={setBulkRemoveCourseModal}
          onRemoveSuccess={handleRemoveSuccess}
        />
      ) : null}
      {archiveCourseModal ? (
        <ArchiveCourseModal
          course={archiveCourseModal}
          onOpenChange={setArchiveCourseModal}
          onArchiveSuccess={refetchData}
        />
      ) : null}
    </BasicLayout>
  )
}

export default CoursesManagementListView
